import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const CSV_TEMPLATE = `title,author,description,price,category,frameworks,cover_image,external_link,is_new,is_bestseller
"Book Title","Author Name","Book description here",9.99,business,"Strategy, Leadership","/assets/images/cover.jpg","",true,false`;

const JSON_TEMPLATE = JSON.stringify([
  {
    title: 'Book Title',
    author: 'Author Name',
    description: 'Book description here',
    price: 9.99,
    category: 'business',
    frameworks: ['Strategy', 'Leadership'],
    cover_image: '/assets/images/cover.jpg',
    external_link: '',
    is_new: true,
    is_bestseller: false,
  }
], null, 2);

const parseCSV = (text) => {
  const lines = text?.trim()?.split('\n');
  if (lines?.length < 2) throw new Error('CSV must have a header row and at least one data row.');
  const headers = lines?.[0]?.split(',')?.map((h) => h?.trim()?.replace(/^"|"$/g, ''));
  return lines?.slice(1)?.map((line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line?.length; i++) {
      if (line?.[i] === '"') { inQuotes = !inQuotes; }
      else if (line?.[i] === ',' && !inQuotes) { values?.push(current); current = ''; }
      else { current += line?.[i]; }
    }
    values?.push(current);
    const obj = {};
    headers?.forEach((h, i) => { obj[h] = values?.[i]?.trim() || ''; });
    return obj;
  });
};

const normalizeBook = (raw) => ({
  title: raw?.title || '',
  author: raw?.author || '',
  description: raw?.description || '',
  price: parseFloat(raw?.price) || 0,
  category: raw?.category || 'business',
  frameworks: Array.isArray(raw?.frameworks)
    ? raw?.frameworks
    : (raw?.frameworks ? raw?.frameworks?.split(',')?.map((f) => f?.trim())?.filter(Boolean) : []),
  cover_image: raw?.cover_image || '/assets/images/no_image.png',
  cover_image_alt: raw?.cover_image_alt || (raw?.title + ' book cover'),
  external_link: raw?.external_link || null,
  is_new: raw?.is_new === true || raw?.is_new === 'true',
  is_bestseller: raw?.is_bestseller === true || raw?.is_bestseller === 'true',
});

const BulkUploadModal = ({ onClose, onUploaded }) => {
  const [tab, setTab] = useState('csv');
  const [fileContent, setFileContent] = useState('');
  const [parsedBooks, setParsedBooks] = useState([]);
  const [parseError, setParseError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev?.target?.result;
      setFileContent(text);
      parseContent(text, tab);
    };
    reader?.readAsText(file);
  };

  const parseContent = (text, format) => {
    setParseError('');
    setParsedBooks([]);
    try {
      let books;
      if (format === 'csv') {
        const rows = parseCSV(text);
        books = rows?.map(normalizeBook);
      } else {
        const parsed = JSON.parse(text);
        books = (Array.isArray(parsed) ? parsed : [parsed])?.map(normalizeBook);
      }
      const valid = books?.filter((b) => b?.title && b?.author && b?.price > 0);
      if (valid?.length === 0) throw new Error('No valid books found. Ensure title, author, and price are set.');
      setParsedBooks(valid);
    } catch (err) {
      setParseError(err?.message || 'Failed to parse file.');
    }
  };

  const handleTextChange = (e) => {
    setFileContent(e?.target?.value);
    if (e?.target?.value?.trim()) parseContent(e?.target?.value, tab);
    else { setParsedBooks([]); setParseError(''); }
  };

  const handleUpload = async () => {
    if (parsedBooks?.length === 0) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const { data, error } = await supabase?.from('ebooks')?.insert(parsedBooks)?.select();
      if (error) throw new Error(error.message);
      setUploadResult({ success: data?.length || 0, failed: 0 });
      onUploaded();
    } catch (err) {
      setUploadResult({ success: 0, failed: parsedBooks?.length, error: err?.message });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const content = tab === 'csv' ? CSV_TEMPLATE : JSON_TEMPLATE;
    const mime = tab === 'csv' ? 'text/csv' : 'application/json';
    const ext = tab === 'csv' ? 'csv' : 'json';
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `books-template.${ext}`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-lg font-heading font-semibold text-foreground">Bulk Upload Books</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Format tabs */}
          <div className="flex gap-2">
            {['csv', 'json']?.map((t) => (
              <button
                key={t}
                onClick={() => { setTab(t); setFileContent(''); setParsedBooks([]); setParseError(''); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tab === t ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {t?.toUpperCase()} Format
              </button>
            ))}
            <button
              onClick={downloadTemplate}
              className="ml-auto flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="Download" size={14} /> Template
            </button>
          </div>

          {/* File upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Upload File</label>
            <input
              type="file"
              accept={tab === 'csv' ? '.csv,text/csv' : '.json,application/json'}
              onChange={handleFileChange}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
            />
          </div>

          {/* Or paste */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Or Paste Content</label>
            <textarea
              value={fileContent}
              onChange={handleTextChange}
              rows={6}
              placeholder={tab === 'csv' ? 'Paste CSV content here...' : 'Paste JSON content here...'}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
            />
          </div>

          {/* Parse error */}
          {parseError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <Icon name="AlertCircle" size={16} color="#ef4444" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600">{parseError}</p>
            </div>
          )}

          {/* Preview */}
          {parsedBooks?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Preview: {parsedBooks?.length} book{parsedBooks?.length !== 1 ? 's' : ''} ready to upload
              </p>
              <div className="border border-border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium">Title</th>
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium">Author</th>
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium">Price</th>
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium">Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedBooks?.map((b, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-2 text-foreground truncate max-w-[200px]">{b?.title}</td>
                        <td className="px-3 py-2 text-muted-foreground">{b?.author}</td>
                        <td className="px-3 py-2 text-muted-foreground">${b?.price}</td>
                        <td className="px-3 py-2 text-muted-foreground">{b?.category}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Upload result */}
          {uploadResult && (
            <div className={`p-3 rounded-lg border ${
              uploadResult?.success > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}>
              {uploadResult?.success > 0 ? (
                <p className="text-sm text-green-700">
                  <Icon name="CheckCircle" size={14} className="inline mr-1" />
                  Successfully uploaded {uploadResult?.success} book{uploadResult?.success !== 1 ? 's' : ''}!
                </p>
              ) : (
                <p className="text-sm text-red-600">
                  <Icon name="XCircle" size={14} className="inline mr-1" />
                  Upload failed: {uploadResult?.error}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              {uploadResult?.success > 0 ? 'Close' : 'Cancel'}
            </button>
            <button
              onClick={handleUpload}
              disabled={parsedBooks?.length === 0 || uploading || uploadResult?.success > 0}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <><Icon name="Loader2" size={16} className="animate-spin" /> Uploading...</>
              ) : (
                <><Icon name="Upload" size={16} /> Upload {parsedBooks?.length > 0 ? parsedBooks?.length : ''} Books</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
